import { DURATION, PACKAGE_TYPE } from '@src/constants/package';
import {
  VOUCHER_CURRENCY_UNIT,
  VOUCHER_REGION,
  VOUCHER_TYPE,
} from '@src/constants/voucher';
import { checkVietNam } from '@src/utils/checkCountry';
import moment from 'moment';
import { COUNTRY } from '@src/constants';
import { PAYMENT_HUB_RENEWAL_TYPE } from '@src/constants/subscription';

export const verifyPolicy = (voucher = {}, currPackageId) => {
  const { policy } = voucher;

  const isSatisfiedPackage =
    policy?.applyAllPackages ||
    policy?.packages?.find((pkg) => pkg.id === currPackageId);

  return isSatisfiedPackage;
};

export const verifyTime = (voucher = {}) => {
  const { endTime, startTime } = voucher;

  const isNotSatisfiedTime =
    moment().isBefore(startTime) || moment().isAfter(endTime);

  return !isNotSatisfiedTime;
};

export const getSavedPrice = ({ voucher, currPrice, currUsdPrice }) => {
  if (!voucher) return 0;

  const isVietNam = checkVietNam();
  const { type, value } = voucher;
  if (type === VOUCHER_TYPE.PERCENT) {
    let savedPrice = isVietNam
      ? (currPrice * value) / 100
      : (currUsdPrice * value) / 100;

    // Round to nearest 1000 for VND and round to nearest 0.01 for USD

    if (isVietNam) {
      savedPrice = Math.ceil(savedPrice / 1000) * 1000;
    } else {
      const savedPriceRounded = Math.round(currUsdPrice - savedPrice);
      const finalUsdPrice =
        savedPriceRounded > 0.01 ? savedPriceRounded - 0.01 : savedPriceRounded;
      savedPrice = currUsdPrice - finalUsdPrice;
    }

    return savedPrice;
  }
  if (type === VOUCHER_TYPE.PRICE) return value;
  return 0;
};

export const getSavedPriceBasePrice = ({
  voucher,
  currPrice,
  currUsdPrice,
}) => {
  if (!voucher) return 0;

  const isVietNam = checkVietNam();
  const { type, value } = voucher;
  if (type === VOUCHER_TYPE.PERCENT) {
    let savedPrice = isVietNam
      ? (currPrice * value) / 100
      : (currUsdPrice * value) / 100;

    // Round to nearest 1000 for VND and round to nearest 0.01 for USD

    if (isVietNam) {
      savedPrice = Math.ceil(savedPrice / 1000) * 1000;
    } else {
      const savedPriceRounded = currUsdPrice - savedPrice;
      savedPrice = currUsdPrice - savedPriceRounded;
    }

    return savedPrice;
  }
  if (type === VOUCHER_TYPE.PRICE) return value;
  return 0;
};

export const checkDiscount = (data) => {
  const { startAt, endAt } = data.discountConfig || {};
  const inDiscountTime = startAt && moment().isBetween(startAt, endAt);

  if (inDiscountTime) return true;
  return false;
};

export const getCharactersExtention = ({ voucher, currCharacters }) => {
  if (!voucher) return 0;

  const { type, value } = voucher;
  if (type === VOUCHER_TYPE.CHARACTER) {
    return Math.ceil((currCharacters * value) / 100);
  }
  return 0;
};

export const getTimeExtention = ({ voucher, currTime }) => {
  if (!voucher) return 0;

  const { type, value } = voucher;
  if (type === VOUCHER_TYPE.PERCENT_TIME)
    return Math.ceil((currTime * value) / 100);

  if (type === VOUCHER_TYPE.TIME) return value;

  return 0;
};

export const checkVoucherByProduct = ({ voucher, product }) => {
  const isVoucherOnlyForAutoRenew =
    voucher?.policy?.renewalType === PAYMENT_HUB_RENEWAL_TYPE.AUTO_RENEW;
  if (isVoucherOnlyForAutoRenew) return false;
  if (voucher?.policy?.applyAllPackages) return true;
  const numberListPackageOfVoucher = voucher?.policy?.packages.filter((pkg) =>
    pkg.code.includes(product),
  ).length;

  return numberListPackageOfVoucher > 0 && !isVoucherOnlyForAutoRenew;
};

export const checkVoucherByProductForHeaderBanner = ({ voucher, product }) => {
  const numberOfListPackagesAPI = voucher?.policy?.packages.filter((pkg) =>
    pkg.code.includes(PACKAGE_TYPE.API),
  ).length;
  if (product === PACKAGE_TYPE.STUDIO && numberOfListPackagesAPI > 0) {
    return true;
  }
  return checkVoucherByProduct({ voucher, product });
};

export const getVouchersByProduct = ({ vouchers = [], product = '' }) => {
  const result =
    vouchers.length > 0 &&
    vouchers.filter((voucher) => checkVoucherByProduct({ voucher, product }));
  return result.length > 0 ? result : [];
};

export const validateVoucherForVN = ({ region, type, currencyUnit }) => {
  const isValidRegion =
    !region || region === VOUCHER_REGION.ALL || region === VOUCHER_REGION.VN;

  const isValidCurrencyUnit =
    type !== VOUCHER_TYPE.PRICE || currencyUnit === VOUCHER_CURRENCY_UNIT.VND;

  return isValidRegion && isValidCurrencyUnit;
};

export const validateVoucherForNonVN = ({ region, type, currencyUnit }) => {
  const isValidRegion =
    !region ||
    region === VOUCHER_REGION.ALL ||
    region === VOUCHER_REGION.NON_VN;

  const isValidCurrencyUnit =
    type !== VOUCHER_TYPE.PRICE || currencyUnit === VOUCHER_CURRENCY_UNIT.USD;

  return isValidRegion && isValidCurrencyUnit;
};

export const validateVoucherByRegion = ({ voucher, country }) => {
  const {
    type,
    policy: { region = '' },
    currencyUnit = VOUCHER_CURRENCY_UNIT.VND,
  } = voucher;
  if (country === COUNTRY.VN) {
    return validateVoucherForVN({ region, type, currencyUnit });
  }
  return validateVoucherForNonVN({ region, type, currencyUnit });
};

export const getPriceFromVoucher = ({
  isVietNam,
  voucher,
  currPrice,
  currUsdPrice,
  duration,
}) => {
  if (!voucher) return isVietNam ? currPrice : currUsdPrice;
  // if not have duration mean not calculate price per month
  if (!duration || voucher.type === VOUCHER_TYPE.PERCENT) {
    const savedMoney = getSavedPrice({ voucher, currPrice, currUsdPrice });
    const price = isVietNam
      ? currPrice - savedMoney
      : currUsdPrice - savedMoney;
    return Math.max(0, price);
  }

  if (isVietNam) {
    let savedVietNamePrice;

    switch (duration) {
      case DURATION.MONTHLY:
        savedVietNamePrice = voucher.value;
        break;
      case DURATION.QUARTERLY:
        savedVietNamePrice = Math.floor(voucher.value / 3 / 1000) * 1000;
        break;
      case DURATION.YEARLY:
        savedVietNamePrice = Math.floor(voucher.value / 12 / 1000) * 1000;
        break;
      default:
        savedVietNamePrice = voucher.value;
    }

    const price = currPrice - savedVietNamePrice;

    return Math.max(0, price);
  }
  let savedForeignPrice;

  switch (duration) {
    case DURATION.MONTHLY:
      savedForeignPrice = parseFloat(voucher.value).toFixed(2);
      break;
    case DURATION.QUARTERLY:
      savedForeignPrice = parseFloat(voucher.value / 3).toFixed(2);
      break;
    case DURATION.YEARLY:
      savedForeignPrice = parseFloat(voucher.value / 12).toFixed(2);
      break;
    default:
      savedForeignPrice = parseFloat(voucher.value).toFixed(2);
  }

  const price = currUsdPrice - savedForeignPrice;

  return Math.max(0, price);
};
